/**
 * applicantsApi.js
 *
 * Talks to getMyApplicantInvites, which runs the "every household I've been
 * added to as a co-applicant" collection-group lookup server-side via the
 * Admin SDK — bypassing firestore.rules entirely rather than fighting
 * Firestore's list/collection-group security-rule constraints from the
 * client SDK.
 *
 * Auth: goes through apiFetch, which sends the HttpOnly firebaseToken
 * cookie set by the /login Lambda and transparently refreshes it (or signs
 * the user out) if the cookie has gone stale.
 */
import { apiFetch } from "./apiFetch";

const API_BASE_URL =
  import.meta.env.VITE_APP_API_URL || "https://auth.ezron.ai";

/**
 * @returns {Promise<Array<object>>} Every applicants/{id} sub-doc (any
 *  household) whose invite email matches the caller, newest first.
 */
export async function getMyApplicantInvites() {
  const res = await apiFetch(`${API_BASE_URL}/getMyApplicantInvites`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `getMyApplicantInvites returned ${res.status}`);
  }
  const data = await res.json();
  return (data.invites || [])
    .map((inv) => ({
      ...inv,
      createdAt: inv.createdAt ? new Date(inv.createdAt) : null,
      updatedAt: inv.updatedAt ? new Date(inv.updatedAt) : null,
    }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

/**
 * Every additional adult occupant on one household application — used by
 * both the primary applicant's own "Household" card and the PM's
 * expanded-row view. Runs server-side via the Admin SDK (same escape hatch
 * as getMyApplicantInvites) rather than a direct client Firestore list,
 * which kept returning "Missing or insufficient permissions" live even on
 * freshly-seeded, correctly-fielded sub-docs for both the primary applicant
 * and the PM — see checkr-screening.md repo memory.
 * @param {string} applicationId
 * @returns {Promise<Array<object>>}
 */
export async function getApplicantsForApplication(applicationId) {
  const res = await apiFetch(`${API_BASE_URL}/getApplicantsForApplication?applicationId=${encodeURIComponent(applicationId)}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `getApplicantsForApplication returned ${res.status}`);
  }
  const data = await res.json();
  return (data.applicants || []).map((a) => ({
    ...a,
    createdAt: a.createdAt ? new Date(a.createdAt) : null,
    updatedAt: a.updatedAt ? new Date(a.updatedAt) : null,
  }));
}

